import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isAtsEmbedActive } from '@/hoc/api/ats-embed';
const PIXEL_ID = (process.env.META_PIXEL_ID || '').trim();
const PIXEL_ENABLED = /^\d+$/.test(PIXEL_ID);
const GA_ID = (process.env.GA_MEASUREMENT_ID || '').trim();
const GA_ENABLED = /^G-[A-Z0-9]+$/.test(GA_ID);
const GTM_ID = (process.env.GTM_CONTAINER_ID || '').trim();
const GTM_ENABLED = /^GTM-[A-Z0-9]+$/.test(GTM_ID);
const ANY_ENABLED = PIXEL_ENABLED || GA_ENABLED || GTM_ENABLED;
const PUBLIC_PATHS = [
    '/ai-interview',
    '/ai-interview/about',
    '/ai-interview/contact',
    '/careers',
    '/career-guide',
    '/overview',
    '/signup',
    '/login',
    '/privacy-policy',
    '/terms-of-use',
];
const UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'fbclid',
    'gclid',
];
const UTM_STORAGE_KEY = 'vriti_utm';
function isPublicPath(pathname, isLoggedIn) {
    // '/' is the landing for everyone except a signed-in ATS embed (the dashboard).
    if (pathname === '/')
        return !(isLoggedIn && isAtsEmbedActive());
    const path = pathname.replace(/\/+$/, '');
    return PUBLIC_PATHS.includes(path) || path === '/blog' || path.startsWith('/blog/');
}
/*
 * VA-29217: GTM runs on the public site AND the signed-in recruiter app (the marketing
 * team's GTM container tracks the whole funnel), but never on the candidate pages,
 * which run the camera, screen share and proctoring, and never inside an ATS embed.
 */
const CANDIDATE_PATH_PREFIXES = ['/interview/', '/select-preferred-slots/'];
function isGtmPath(pathname) {
    if (isAtsEmbedActive())
        return false;
    return !CANDIDATE_PATH_PREFIXES.some((p) => pathname.startsWith(p));
}
/** Meta's standard base snippet, minus the inline PageView (fired per route below). */
function loadPixel() {
    if (window.fbq)
        return;
    const fbq = function (...args) {
        if (fbq.callMethod)
            fbq.callMethod(...args);
        else
            fbq.queue.push(args);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
    window._fbq = fbq;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    fbq('init', PIXEL_ID);
}
/** Google's standard gtag.js snippet. page_view is sent manually per route. */
function loadGa() {
    if (window.gtag)
        return;
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        var _a;
        // gtag.js requires the `arguments` object itself, not a copied array.
        // eslint-disable-next-line prefer-rest-params
        (_a = window.dataLayer) === null || _a === void 0 ? void 0 : _a.push(arguments);
    };
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(script);
    window.gtag('js', new Date());
    window.gtag('config', GA_ID, { send_page_view: false });
}
let gtmLoaded = false;
/** Google's standard GTM snippet. Its <noscript> iframe is left out: this SPA needs JS. */
function loadGtm() {
    if (gtmLoaded)
        return;
    gtmLoaded = true;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    document.head.appendChild(script);
}
function captureUtm(search) {
    const params = new URLSearchParams(search);
    const found = {};
    UTM_KEYS.forEach((k) => {
        const v = params.get(k);
        if (v)
            found[k] = v;
    });
    if (!Object.keys(found).length)
        return;
    try {
        sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(found));
    }
    catch (_a) {
        // Storage blocked (private mode etc.) — attribution is best-effort.
    }
}
export function readUtm() {
    try {
        return JSON.parse(sessionStorage.getItem(UTM_STORAGE_KEY) || '{}');
    }
    catch (_a) {
        return {};
    }
}
/** Sends one event to Meta Pixel and GA4, under each platform's standard name. */
function track(names, params = {}) {
    if (PIXEL_ENABLED && window.fbq)
        window.fbq('track', names.meta, params);
    if (GA_ENABLED && window.gtag)
        window.gtag('event', names.ga, params);
}
/*
 * VA-29217: GTM events follow the marketing team's dataLayer brief. Their GTM
 * triggers match these event and parameter names exactly, so do not rename them
 * without telling marketing. Never put email, name, phone or password in params.
 * No page_view is pushed: GTM tracks route changes with its History Change trigger.
 */
function newEventId() {
    return `ev_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}
function pushGtm(event, params = {}) {
    var _a;
    if (!gtmLoaded)
        return;
    (_a = window.dataLayer) === null || _a === void 0 ? void 0 : _a.push(Object.assign({ event, event_id: newEventId(), page_path: window.location.pathname }, params));
}
/** Call once from the signup success handler, after the server has created the organization. */
export function trackSignupComplete() {
    track({ meta: 'CompleteRegistration', ga: 'sign_up' }, Object.assign({ content_name: 'organization_signup', method: 'email' }, readUtm()));
    // VA-29219: step 1 ("Account") is the call that creates the organization, so both fire here.
    pushGtm('sign_up_step1_complete', { form_id: 'signup', step: 1 });
    pushGtm('sign_up', { method: 'email', user_type: 'recruiter' });
}
/** VA-29227: call only after the Contact page inquiry API has accepted the inquiry. */
export function trackDemoRequest() {
    pushGtm('demo_request', { form_id: 'contact', lead_source: 'website' });
}
const SIGNUP_FIELDS = [
    'fullName',
    'companyName',
    'companyUrl',
    'email',
    'password',
    'confirmPassword',
];
/** VA-29219: mount on the signup page. Pushes sign_up_start on the first focus of a form field. */
export function useSignupStartTracking() {
    useEffect(() => {
        if (!GTM_ENABLED)
            return;
        const onFocus = (e) => {
            var _a;
            const name = (_a = e.target) === null || _a === void 0 ? void 0 : _a.name;
            if (!name || !SIGNUP_FIELDS.includes(name))
                return;
            document.removeEventListener('focusin', onFocus);
            pushGtm('sign_up_start', { form_id: 'signup', first_field: name });
        };
        document.addEventListener('focusin', onFocus);
        return () => document.removeEventListener('focusin', onFocus);
    }, []);
}
/** VA-29218: a CTA's visible label. Skips aria-hidden copies (the landing Button renders its label twice for the roll animation). */
function ctaText(el) {
    const label = el.getAttribute('aria-label');
    if (label)
        return label.trim().slice(0, 60);
    const copy = el.cloneNode(true);
    copy.querySelectorAll('[aria-hidden="true"]').forEach((n) => n.remove());
    return (copy.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 60);
}
/** VA-29218: where on the page a CTA sits, for cta_click. */
function ctaLocation(el) {
    // Footer first: the footer's link columns are <nav> elements too.
    if (el.closest('footer'))
        return 'footer';
    if (el.closest('header, nav'))
        return 'header';
    const section = el.closest('section');
    return ((section === null || section === void 0 ? void 0 : section.id) ||
        (section === null || section === void 0 ? void 0 : section.getAttribute('aria-labelledby')) ||
        (section === null || section === void 0 ? void 0 : section.getAttribute('aria-label')) ||
        'body');
}
/** Mount once inside the router. Loads the tags for the current route and tracks CTA clicks. */
export function useMarketingTracking(isLoggedIn) {
    const { pathname, search } = useLocation();
    useEffect(() => {
        if (GTM_ENABLED && isGtmPath(pathname))
            loadGtm();
        if (!isPublicPath(pathname, isLoggedIn))
            return;
        captureUtm(search);
        if (PIXEL_ENABLED)
            loadPixel();
        if (GA_ENABLED)
            loadGa();
        // Runs after the page's own usePageMeta (child effects run first), so the
        // title reported here is the new route's.
        track({ meta: 'PageView', ga: 'page_view' }, GA_ENABLED
            ? { page_location: window.location.href, page_path: pathname, page_title: document.title }
            : {});
    }, [pathname, search, isLoggedIn]);
    // One delegated listener instead of an onClick on every CTA (Nav, Hero, Closing,
    // overview Final): any click on a link to /signup counts as a Lead, and as a GTM
    // cta_click; "Talk to sales" style links are a cta_click too (VA-29218).
    useEffect(() => {
        if (!ANY_ENABLED)
            return;
        const onClick = (e) => {
            var _a, _b;
            const link = (_b = (_a = e.target) === null || _a === void 0 ? void 0 : _a.closest) === null || _b === void 0 ? void 0 : _b.call(_a, 'a[href]');
            if (!link)
                return;
            const text = ctaText(link);
            const isSignup = link.origin === window.location.origin &&
                link.pathname === '/signup' &&
                window.location.pathname !== '/signup';
            const isSales = /talk to sales|book a demo/i.test(text) ||
                /\/(sales|demo|contact)\/?$/i.test(link.pathname);
            if (isSignup) {
                track({ meta: 'Lead', ga: 'generate_lead' }, { content_name: 'signup_cta', source_page: window.location.pathname });
            }
            if (isSignup || isSales) {
                pushGtm('cta_click', {
                    cta_type: isSignup ? 'signup' : 'sales',
                    cta_text: text,
                    cta_location: ctaLocation(link),
                });
            }
        };
        document.addEventListener('click', onClick, true);
        return () => document.removeEventListener('click', onClick, true);
    }, []);
}
